/*
 * ===============================================================================================================
 *                                Copyright 2021, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import React, { useCallback, useMemo, useEffect, useState, ChangeEvent } from 'react';
import { Box, Typography, IconButton, Popover, Link, TextField } from '@mui/material';
import { Info, AlertCircle } from '@jda/lui-common-icon-library-mui5';
import { ControllerFieldState, ControllerRenderProps } from 'react-hook-form';
import { useStyles } from './styles';
import { getFeatureFlag as useFeatureFlag } from '@jda/lui-portal-utilities';
import {
  ACCESS_APP_LISTINGS_TITLE,
  APP_LISTING_TITLE,
  ERROR_FETCHING_SETTINGS,
  IDP_ROLE_MAPPING_TITLE,
  IDP_ROLE_MAPPING_PERMISSION_TITLE,
  INACTIVITY_TIMEOUT_HEADING,
  ISOLATION,
  ISOLATION_ENABLED,
  ISOLATION_TITLE,
  MINUTES,
  SETTINGS_TIMEOUT_TITLE,
  SETTINGS_WARNING_TITLE,
  BY_USER_STORE_TITLE,
  BY_USER_STORE_DESC,
  ERROR_IN_FETCHING_IDENTITY_PROVIDERS,
  PASSWORD_POLICY,
  PASSWORD_POLICY_LABEL,
  PASSWORD_POLICY_INFO,
  INACTIVITY_TIMEOUT,
  TIMEOUT_WARNING,
  ENABLE_APP_LISTINGS,
  MINUTES_GREATER_THAN_ZERO_ERROR_MSG,
  MAX_LENGTH_VALIDATION,
  NUMBER_REGEXP,
  TIMEOUT_VALIDATION_ERROR_MSG,
  formDefaultValues,
  ERROR_SAVING_SETTINGS,
  SETTINGS_SUCCESS_MESSAGE,
  ENABLE_IDP_ROLE_MAPPING,
  IDP_DISABLE_WARN_MSG,
  ENABLE_BY_USER_STORE,
  MFA_ACTIVE,
  MFA_ACTIVE_LABEL,
  MFA_DISABLE_WARN_MSG,
  INACTIVITY_TIMEOUT_CARD,
  APP_LISTING_CARD,
  ROLE_MAPPING_CARD,
  SEGREGATION_CARD,
  USER_STORE_CARD,
  BY_USER_STORE_ALERT,
  NATIVE_CAMEL_CASE,
  NATIVE_UPPER_CASE,
  LOCAL_CAMEL_CASE,
  B2C_IDP_ID
} from './constants';
import {
  createIdp,
  createIdpForGravitee,
  createRealmSettings,
  getIdpById,
  getUserRealmSettings,
  updateIdp,
  updateRealmSettings,
  toggleMfa
} from '../../services/setting';
import { ErrorMessage } from '../../shared/ErrorMessage/ErrorMessage';
import interpolateString from '../../utility/stringInterpolation';
import SettingsSection from '../../components/Settings/SettingsSection';
import { selectResourcePermissions, selectAllAssignedPermissions } from '../../store/auth/authSelector';
import { displaySnackbar, hideSnackbar } from '../../store/snackBar/snackBarActionCreator';
import { useDispatch, useSelector } from 'react-redux';
import { checkResourcePermission, checkPermission } from '../../shared/CheckPermissions/CheckPermission';
import { actions, permissions, resources } from '../../shared/CheckPermissions/constants';
import { HARNESS_FEATURE_FLAG } from '../../utility/constants';
import { getRealmSettings } from '../../store/realmSettings/realmSettings';
import {
  selectRealmSettings,
  selectRealmSettingsLoading,
  selectRealmSettingsError
} from '../../store/realmSettings/realmSettingsSelector';
import SnackbarAlert from '../../shared/SnackbarAlert';
import { fetchPasswordPolicies } from '../../store/passwordPolicies/passwordPolicies';
import { selectPasswordPolicies } from '../../store/passwordPolicies/passwordPoliciesSelector';
import { POLICY_DETAILS } from '../../components/PasswordPolicies/PasswordPolicyTable/PasswordPolicyDetails/constants';
import DetailsModal from '../../shared/Dialog/DetailsModal';
import PasswordPolicyDetails from '../../components/PasswordPolicies/PasswordPolicyTable/PasswordPolicyDetails/PasswordPolicyDetails';
import { ISetting } from '../../models/Settings/SettingContract';
import { ALLOW_ACCESS, DISALLOW_ACCESS } from '../../components/Settings/constants';
import { isPortalV2 } from '../../utility/commonUtility';
import { fetchIdps } from '../../store/user/users';
import { getRealmInfo } from '../../services/setting';
import { MIGRATION_STATUS } from '../../components/AddMachineDialog/contants';



interface MinutesInputProps {
  field: ControllerRenderProps;
  fieldState: ControllerFieldState;
  id: string;
  onAfterChange: () => void;
}

const MinutesInput = ({ field, fieldState, id, onAfterChange }: MinutesInputProps) => {
  const { classes } = useStyles();

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    if (value.length <= MAX_LENGTH_VALIDATION && NUMBER_REGEXP.test(value)) {
      field.onChange(value);
      onAfterChange();
    }
  };

  return (
    <Box className={classes.textInput}>
      <TextField
        {...field}
        id={id}
        variant="standard"
        className={classes.input}
        onChange={handleChange}
        error={fieldState.invalid}
        inputProps={{ className: classes.input }}
      />
      <Typography>{MINUTES}</Typography>
    </Box>
  );
};

const Settings = () => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const [byUserStore, setByUserStore] = useState(false);
  const [nativeIdp, setNativeIdp] = useState<any>();
  const [b2cIdp, setB2cIdp] = useState<any>();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [openPolicyDetails, setOpenPolicyDetails] = React.useState(false);
  const [realmDetails, setRealmDetails] = useState<any>({});
  const realmSettings = useSelector(selectRealmSettings);
  const showLoader = useSelector(selectRealmSettingsLoading);
  const error = useSelector(selectRealmSettingsError);
  const assignedPermissions = useSelector(selectAllAssignedPermissions);
  const resourcePermissions = useSelector(selectResourcePermissions);
  const passwordPolicies = useSelector(selectPasswordPolicies);
  const graviteeFeatureFlag = useFeatureFlag(HARNESS_FEATURE_FLAG.graviteeEnablement);
  const isGraviteeFFEnabled = isPortalV2() ? true : graviteeFeatureFlag;
  const isLocationEnabled = useFeatureFlag(HARNESS_FEATURE_FLAG.location);
  const isMfaEnabled = useFeatureFlag(HARNESS_FEATURE_FLAG.mfaEnablement);

  const settingsValues = useMemo(
    () => ({
      ...realmSettings,
      inactivityTimeout: Math.floor(realmSettings.inactivityTimeout / 60),
      timeoutWarning: Math.floor(realmSettings.timeoutWarning / 60)
    }),
    [realmSettings]
  );

  const resourceNotFound = error?.response?.status === 404;
  const isNativeIdpEnabled = nativeIdp?.enabled ?? false;
  const isMfaActive = nativeIdp?.mfaActive ?? true;

  const passwordPolicyViewPermission =
    resourcePermissions &&
    resourcePermissions.resources &&
    checkResourcePermission(actions.VIEW, resourcePermissions.resources[resources.PASSWORD_POLICY]);
  const idpViewPermission =
    resourcePermissions &&
    resourcePermissions.resources &&
    checkResourcePermission(actions.VIEW, resourcePermissions.resources[resources.IDENTITY_PROVIDER]);
  const idpEditPermission =
    resourcePermissions &&
    resourcePermissions.resources &&
    checkResourcePermission(actions.EDIT, resourcePermissions.resources[resources.IDENTITY_PROVIDER]);
  const mfaEditPermission =
    resourcePermissions &&
    resourcePermissions.resources &&
    checkResourcePermission(actions.EDIT, resourcePermissions.resources[resources.MFA]);
  const settingsReadWritePermission =
    assignedPermissions &&
    assignedPermissions.permissions &&
    checkPermission([permissions.realm_settings.read_write], assignedPermissions.permissions);

  const isNativeIdpAllowed = Boolean(isGraviteeFFEnabled && idpViewPermission);
  const arePasswordPoliciesShown = Boolean(isNativeIdpEnabled && passwordPolicyViewPermission);
  const isSettingsEditAllowed = Boolean(settingsReadWritePermission);

  const [isIdpLoading, setIsIdpLoading] = useState(isNativeIdpAllowed);

  const open = Boolean(anchorEl);

  const id = open ? 'transitions-popper' : undefined;
  let currentPolicy: any = { name: '' };

  if (passwordPolicies && passwordPolicies.length > 0) {
    const realmPolicy = passwordPolicies.find((entity: any) => entity.idpsNames?.length > 0);
    const defaultPolicy = passwordPolicies.find((entity: any) => entity.isDefault === true);
    currentPolicy = realmPolicy || defaultPolicy;
  }
  const getRealmDetails = useCallback(async () => {
    const realmDetails = await getRealmInfo();
    setRealmDetails(realmDetails);
  }, []);
  useEffect(() => {
    getRealmSettings(dispatch);
    getRealmDetails();
  }, [dispatch]);

  const getAllRealmIdps = useCallback(() => {
    setIsIdpLoading(true);
    fetchIdps(dispatch)
      .then(async (idps: any) => {
        const isLocalIDPEnabled = idps.filter((idp: any) => idp.idpType === LOCAL_CAMEL_CASE);
        const isNativeIdpEnabled = idps.filter((idp: any) => idp.idpType === NATIVE_CAMEL_CASE || idp.type === NATIVE_UPPER_CASE);
        if (idps.length > 0) {
          setByUserStore(!!isLocalIDPEnabled.length);
        }
        if (!isLocalIDPEnabled.length && isNativeIdpEnabled.length > 0) {
          const nativeIdp = idps.find((idp: any) => (idp.idpType === NATIVE_CAMEL_CASE || idp.type === NATIVE_UPPER_CASE));
          let nativeIdpDetails = {};
          if (realmDetails?.migrationState !== MIGRATION_STATUS.MODERN) {
            const defaultSourceIdpId = nativeIdp?.idpSchema?.externalIdentityProviders?.find((idp: any) => idp.default);
            nativeIdpDetails = defaultSourceIdpId ? await getIdpById(defaultSourceIdpId.default) : undefined;
            setB2cIdp(nativeIdp.id);
          } else {
            nativeIdpDetails = nativeIdp;
          }
          setNativeIdp(nativeIdpDetails);
        }
        setIsIdpLoading(false);
      })
      .catch((_error: any) => {
        dispatch(displaySnackbar(ERROR_IN_FETCHING_IDENTITY_PROVIDERS, 'error'));
        setIsIdpLoading(false);
      });
  }, [dispatch, realmDetails.migrationState]);

  useEffect(() => {
    isNativeIdpAllowed && getAllRealmIdps();
  }, [isNativeIdpAllowed, getAllRealmIdps]);

  useEffect(() => {
    arePasswordPoliciesShown && fetchPasswordPolicies(dispatch);
  }, [dispatch, arePasswordPoliciesShown]);

  const editSettings = async (formValues: Partial<ISetting>) => {
    try {
      dispatch(hideSnackbar());

      const realmSettings = await getUserRealmSettings().catch(error => {
        if (error?.response?.status === 404) {
          return formDefaultValues;
        }

        throw error;
      });

      const requestObject = {
        ...realmSettings,
        ...formValues
      };

      if (realmSettings.id) {
        await updateRealmSettings(requestObject);
      } else {
        await createRealmSettings(requestObject);
      }

      getRealmSettings(dispatch);
      dispatch(displaySnackbar(SETTINGS_SUCCESS_MESSAGE, 'success'));
    } catch (error: any) {
      throw new Error(interpolateString([error.response?.data?.message], ERROR_SAVING_SETTINGS));
    }
  };

  const handleUserStoreSubmit = async (formValues: Record<string, any>) => {
    try {
      dispatch(hideSnackbar());

      if (formValues[ENABLE_BY_USER_STORE] !== undefined && formValues[ENABLE_BY_USER_STORE] !== isNativeIdpEnabled) {
        if (nativeIdp) {
          const payload: any = { enabled: formValues[ENABLE_BY_USER_STORE] };
          if (realmDetails?.migrationState !== MIGRATION_STATUS.MODERN && b2cIdp) {
            payload[B2C_IDP_ID] = b2cIdp;
          }
          await updateIdp(nativeIdp.id, payload);
        } else {

          realmDetails?.migrationState === MIGRATION_STATUS.MODERN ? await createIdpForGravitee({ type: NATIVE_UPPER_CASE }) : await createIdp({ identityProviderType: NATIVE_CAMEL_CASE });
        }
      }

      if (formValues[MFA_ACTIVE] !== undefined && formValues[MFA_ACTIVE] !== isMfaActive) {
        await toggleMfa(formValues[MFA_ACTIVE]);
      }

      getAllRealmIdps();
      dispatch(displaySnackbar(SETTINGS_SUCCESS_MESSAGE, 'success'));
    } catch (error: any) {
      throw new Error(interpolateString([error.response?.data?.message], ERROR_SAVING_SETTINGS));
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };
  const renderPolicyDetails = () => { setOpenPolicyDetails(true); };

  return (
    <Box className={classes.root}>
      <Box className={classes.realmSettings}>
        <SettingsSection
          title={INACTIVITY_TIMEOUT_HEADING}
          fields={[
            {
              name: INACTIVITY_TIMEOUT,
              value: `${settingsValues[INACTIVITY_TIMEOUT]}`,
              label: SETTINGS_TIMEOUT_TITLE,
              displayValue: `${settingsValues[INACTIVITY_TIMEOUT]} ${MINUTES}`,
              controllerProps: {
                rules: {
                  required: MINUTES_GREATER_THAN_ZERO_ERROR_MSG,
                  min: { value: 1, message: MINUTES_GREATER_THAN_ZERO_ERROR_MSG },
                  validate: (inactivityTimeout, formValues) =>
                    Number(inactivityTimeout) >= Number(formValues[TIMEOUT_WARNING]) || TIMEOUT_VALIDATION_ERROR_MSG
                }
              },
              renderField: ({ field, fieldState, trigger }) => (
                <MinutesInput
                  field={field}
                  fieldState={fieldState}
                  id={INACTIVITY_TIMEOUT}
                  onAfterChange={() => {
                    trigger(TIMEOUT_WARNING);
                  }}
                />
              )
            },
            {
              name: TIMEOUT_WARNING,
              value: `${settingsValues[TIMEOUT_WARNING]}`,
              label: SETTINGS_WARNING_TITLE,
              displayValue: `${settingsValues[TIMEOUT_WARNING]} ${MINUTES}`,
              controllerProps: {
                rules: {
                  required: MINUTES_GREATER_THAN_ZERO_ERROR_MSG,
                  min: { value: 1, message: MINUTES_GREATER_THAN_ZERO_ERROR_MSG },
                  validate: (timeoutWarning, formValues) =>
                    Number(timeoutWarning) <= Number(formValues[INACTIVITY_TIMEOUT]) || TIMEOUT_VALIDATION_ERROR_MSG
                }
              },
              renderField: ({ field, fieldState, trigger }) => (
                <MinutesInput
                  field={field}
                  fieldState={fieldState}
                  id={TIMEOUT_WARNING}
                  onAfterChange={() => {
                    trigger(INACTIVITY_TIMEOUT);
                  }}
                />
              )
            }
          ]}
          resourceNotFound={resourceNotFound}
          isEditAllowed={isSettingsEditAllowed}
          onSubmit={async formValues => {
            await editSettings({
              [INACTIVITY_TIMEOUT]: Number(formValues[INACTIVITY_TIMEOUT]) * 60,
              [TIMEOUT_WARNING]: Number(formValues[TIMEOUT_WARNING]) * 60
            });
          }}
          dataTestId={INACTIVITY_TIMEOUT_CARD}
        />
        <SettingsSection
          title={APP_LISTING_TITLE}
          fields={[
            {
              name: ENABLE_APP_LISTINGS,
              value: settingsValues[ENABLE_APP_LISTINGS],
              label: ACCESS_APP_LISTINGS_TITLE
            }
          ]}
          resourceNotFound={resourceNotFound}
          isEditAllowed={isSettingsEditAllowed}
          onSubmit={editSettings}
          dataTestId={APP_LISTING_CARD}
        />
        {realmDetails?.migrationState !== MIGRATION_STATUS.MODERN && <SettingsSection
          title={IDP_ROLE_MAPPING_TITLE}
          fields={[
            {
              name: ENABLE_IDP_ROLE_MAPPING,
              value: settingsValues[ENABLE_IDP_ROLE_MAPPING],
              label: IDP_ROLE_MAPPING_PERMISSION_TITLE
            }
          ]}
          resourceNotFound={resourceNotFound}
          isEditAllowed={isSettingsEditAllowed}
          onSubmit={editSettings}
          dataTestId={ROLE_MAPPING_CARD}
        />
        }
        {
          isLocationEnabled && (
            <SettingsSection
              title={ISOLATION}
              fields={[
                {
                  name: ISOLATION_ENABLED,
                  value: settingsValues[ISOLATION_ENABLED],
                  label: ISOLATION_TITLE
                }
              ]}
              resourceNotFound={resourceNotFound}
              isEditAllowed={isSettingsEditAllowed}
              onSubmit={editSettings}
              dataTestId={SEGREGATION_CARD}
            />
          )
        }
        {
          isNativeIdpAllowed && !byUserStore && (
            <SettingsSection
              isLoading={showLoader || isIdpLoading}
              title={BY_USER_STORE_TITLE}
              fields={[
                {
                  name: ENABLE_BY_USER_STORE,
                  value: isNativeIdpEnabled,
                  label: BY_USER_STORE_DESC,
                  isEditAllowed: () => Boolean(idpEditPermission),
                  renderAfterField: ({ field }) =>
                    isNativeIdpEnabled &&
                    !field.value && (
                      <Box className={classes.alertMsg}>
                        <Box className={classes.warningIcon}>
                          <AlertCircle />
                        </Box>
                        <Typography variant="body1">
                          <Box fontWeight="normal">{IDP_DISABLE_WARN_MSG}</Box>
                        </Typography>
                      </Box>
                    )
                },
                {
                  name: MFA_ACTIVE,
                  value: isMfaActive,
                  label: MFA_ACTIVE_LABEL,
                  displayValue: !isNativeIdpEnabled ? null : isMfaActive ? ALLOW_ACCESS : DISALLOW_ACCESS,
                  isEditAllowed: ({ watch }) => Boolean(isMfaEnabled && mfaEditPermission && watch(ENABLE_BY_USER_STORE)),
                  controllerProps: {
                    shouldUnregister: true
                  },
                  renderAfterField: ({ field }) =>
                    isMfaActive &&
                    !field.value && (
                      <Box className={classes.alertMsg}>
                        <Box className={classes.warningIcon}>
                          <AlertCircle />
                        </Box>
                        <Typography variant="body1">
                          <Box fontWeight="normal">{MFA_DISABLE_WARN_MSG}</Box>
                        </Typography>
                      </Box>
                    )
                },
                {
                  name: PASSWORD_POLICY,
                  value: null,
                  label: PASSWORD_POLICY_LABEL,
                  displayValue: !arePasswordPoliciesShown || !currentPolicy?.name ? null : (
                    <Box className={classes.passwordPolicyDisplayValue}>
                      <Link component="button" className={classes.pointer} onClick={renderPolicyDetails}>
                        {currentPolicy.name}
                      </Link>
                      <IconButton size="small" onClick={handleClick} data-testid={'password-info-button'}>
                        <Info fontSize="small" />
                      </IconButton>
                    </Box>
                  ),
                  isEditAllowed: () => false
                }
              ]}
              isEditAllowed={Boolean(idpEditPermission || (isMfaEnabled && mfaEditPermission && isNativeIdpEnabled))}
              onSubmit={handleUserStoreSubmit}
              dataTestId={USER_STORE_CARD}
              showAlert={true}
              alertMessage={BY_USER_STORE_ALERT}
            />
          )
        }
        <Box className={classes.message}>
          {/* <Loader showLoader={showLoader || isIdpLoading} isCenter customClasses={classes.loader} /> */}
          {error && !resourceNotFound && (
            <ErrorMessage
              message={interpolateString([error.response?.message || error.message], ERROR_FETCHING_SETTINGS)}
            />
          )}
        </Box>
      </Box >
      {arePasswordPoliciesShown && (
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          data-testid={'password-info-prpover'}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
        >
          <Box className={classes.passwordPolicyPopover}>
            <Typography variant="body2" gutterBottom component="div">
              {PASSWORD_POLICY_INFO}
            </Typography>
          </Box>
        </Popover>
      )}
      {
        openPolicyDetails && currentPolicy && (
          <DetailsModal
            openModal={openPolicyDetails}
            handleClose={setOpenPolicyDetails}
            dialogTitle={POLICY_DETAILS}
            maxWidth={'md'}
          >
            <PasswordPolicyDetails passwordPolicyId={currentPolicy.id} />
          </DetailsModal>
        )
      }
      <SnackbarAlert />
    </Box >
  );
};

const Setting = {
  component: Settings,
  name: 'Settings'
};

export default Setting;
